import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postCreateCompanySpecificProduct } from "@/services/Company";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/contexts/AuthContext";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { useTranslation } from "react-i18next";
import { CompanySpecificProduct, CreateCompanySpecificProductDto } from "@/types";
import Heading from "@/components/atoms/Heading";
import ProductTable from "@/components/organisms/SettingsTables/ProductTable";
import Stack from "@/components/atoms/Stack";
import DynamicProductForm from "../../organisms/DynamicProductForm";
import axios from "axios";

const Product = () => {
  const [selectedProduct, setSelectedProduct] = useState<CompanySpecificProduct | null>(null);
  // Bumped on successful create to remount DynamicProductForm — wipes form
  // fields, internal localImages state, and the dropzone in one move.
  const [formKey, setFormKey] = useState(0);
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateCreateProductNew, isPending: isMutating } = useMutation({
    mutationFn: async (data: CreateCompanySpecificProductDto) => {
      return await postCreateCompanySpecificProduct(data);
    },
    onMutate: async (data: CreateCompanySpecificProductDto) => {
      const queryKey = ["getCompanySpecificProduct", currentCompany?.companyId];
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<CompanySpecificProduct[]>({ queryKey });
      const optimisticProduct: CompanySpecificProduct = {
        id: -Date.now(),
        companyId: data.companyId,
        companySpecificProductName: data.companySpecificProductName,
        salesItemId: data.salesItemId,
        properties: data.properties,
        boxTypeId: data.boxTypeId ?? null,
        orderTemplateNetwork: [],
        images: null,
        attachments: null,
        components: [],
        kgPerMeter: data.kgPerMeter ?? null,
        kgPerStk: data.kgPerStk ?? null,
        kgPerM3: data.kgPerM3 ?? null,
        originalProductNumber: data.originalProductNumber,
        deleted: false,
        productComposition: [],
      };
      queryClient.setQueriesData<CompanySpecificProduct[]>({ queryKey }, (old) =>
        old ? [...old, optimisticProduct] : old
      );
      return { snapshots };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.productCreated"),
          "product-created-success"
        )
      );
    },
    onError: (error, _data, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          notifications.show(
            ErrorNotificationObj(
              t("notifications:generic.error"),
              t("validation:duplicateProductName"),
              "product-created-duplicate-error"
            )
          );
        } else {
          notifications.show(
            ErrorNotificationObj(
              t("notifications:generic.error"),
              t("notifications:settings.productCreatedError"),
              "product-created-error"
            )
          );
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCompanySpecificProduct", currentCompany?.companyId],
      });
    },
  });

  return (
    <Stack data-testid="product-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.products")}
      </Heading>
      <DynamicProductForm
        key={formKey}
        defaultSelectedProduct={selectedProduct}
        setDefaultSelectedProduct={setSelectedProduct}
        onSubmitMutation={mutateCreateProductNew}
        isMutating={isMutating}
        clearable
      />
      <ProductTable setSelectedProduct={setSelectedProduct} />
    </Stack>
  );
};

export default Product;
